import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, resolveAsset } from '../../api/client';
import { useT } from '../../i18n';

// Create Series wizard. Scaffolds a series from ONE finished base story: pick
// whether copies share the storyline and/or graphics, how many books, and
// (optionally) prefill the kids' names. Photos, prices, and generation all
// happen afterwards on the management page — the wizard just prepares the infra,
// so it's fine to create the series with blank slots and fill them in later.

export default function SeriesWizard() {
  const t = useT();
  const GENDERS = [
    { value: 'ولد', label: t.adminGenderBoy },
    { value: 'بنت', label: t.adminGenderGirl },
  ];
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromStory = params.get('fromStory');

  const [base, setBase] = useState(null);
  const [loadErr, setLoadErr] = useState('');

  const [step, setStep] = useState(0);
  const [sameStoryline, setSameStoryline] = useState(true);
  const [sameGraphics, setSameGraphics] = useState(true);
  const [count, setCount] = useState(3);
  const [title, setTitle] = useState('');
  const [defaultPrice, setDefaultPrice] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [names, setNames] = useState([]); // optional prefill rows: {name, gender, age}
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!fromStory) { setLoadErr(t.adminBaseStoryNotSet); return; }
    api.get(`/api/admin/stories/${fromStory}`)
      .then((s) => {
        const p = s.personalization || {};
        setBase({ id: s._id, title: s.title, coverImageUrl: s.coverImageUrl, gender: p.gender || 'ولد', age: p.age || '' });
        setTitle(s.title || '');
      })
      .catch((e) => setLoadErr(e.message || t.adminLoadStoryError));
  }, [fromStory]);

  const baseDefaults = useMemo(() => base || {}, [base]);

  // Size the optional prefill list to `count`, keeping filled rows.
  const buildNames = () => {
    const n = Math.max(1, Math.min(60, Number(count) || 1));
    setNames((prev) => {
      const next = [...prev];
      while (next.length < n) next.push({ name: '', gender: baseDefaults.gender || 'ولد', age: baseDefaults.age || '' });
      next.length = n;
      return next;
    });
  };

  const setName = (i, patch) =>
    setNames((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const n = Math.max(1, Math.min(60, Number(count) || 1));
      // Send the prefilled rows if the admin filled any; otherwise just a count
      // of blank slots. Either way the series is created and we go manage it.
      const filled = names.filter((m) => m.name.trim());
      const payload = {
        baseStoryId: base.id,
        title: title.trim(),
        sameStoryline,
        sameGraphics,
        isTest,
        defaultPriceCents: defaultPrice !== '' ? Math.round(Number(defaultPrice) * 100) : 0,
      };
      if (filled.length) {
        payload.members = names.map((m) => ({ name: m.name.trim(), gender: m.gender, age: String(m.age || '').trim() }));
      } else {
        payload.count = n;
      }
      const { seriesId } = await api.post('/api/admin/series', payload);
      navigate(`/admin/series/${seriesId}`);
    } catch (e) {
      setError(e.message || t.adminCreateSeriesError);
      setSubmitting(false);
    }
  };

  if (loadErr) {
    return (
      <div className="admin-page" dir="rtl">
        <h2>{loadErr}</h2>
        <Link to="/admin" className="submit-btn">رجوع</Link>
      </div>
    );
  }
  if (!base) return <div className="admin-page" dir="rtl"><p>جارِ التحميل…</p></div>;

  const steps = ['القصة', 'الرسومات', 'العدد', 'الأطفال (اختياري)'];

  return (
    <div className="admin-page" dir="rtl">
      <h1 className="page-title">إنشاء سلسلة</h1>
      <p className="admin-scene-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {base.coverImageUrl && <img src={resolveAsset(base.coverImageUrl)} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} />}
        من القصة: {base.title || '—'}
      </p>

      <div style={{ display: 'flex', gap: 8, margin: '10px 0 20px', flexWrap: 'wrap' }}>
        {steps.map((label, i) => (
          <button key={label} type="button" onClick={() => setStep(i)}
            className={i === step ? 'admin-filter-btn active' : 'admin-filter-btn'} style={{ cursor: 'pointer' }}>
            {i + 1}. {label}
          </button>
        ))}
      </div>

      {step === 0 && (
        <div className="form-zone" style={{ maxWidth: 640 }}>
          <h2 className="page-title" style={{ fontSize: '1.05rem' }}>القصة (النص)</h2>
          <ChoiceCard active={sameStoryline} onClick={() => setSameStoryline(true)}
            title="نفس القصة" desc="نفس النص تمامًا، مع تبديل الاسم وملاءمة صيغة المذكّر/المؤنّث لكل طفل." />
          <ChoiceCard active={!sameStoryline} onClick={() => setSameStoryline(false)}
            title="قصة جديدة لكل طفل" desc="نفس القالب والفكرة، لكن يكتب الذكاء الاصطناعي نصًّا جديدًا لكل طفل." />
          <StepNav onNext={() => setStep(1)} />
        </div>
      )}

      {step === 1 && (
        <div className="form-zone" style={{ maxWidth: 640 }}>
          <h2 className="page-title" style={{ fontSize: '1.05rem' }}>الرسومات</h2>
          <ChoiceCard active={sameGraphics} onClick={() => setSameGraphics(true)}
            title="نفس الرسومات" desc="نفس الصور تمامًا مع تبديل وجه الطفل — نثبّت صورة القصة الأصلية كمرجع ونطلب من النموذج إعادة إنتاجها بنفس التكوين." />
          <ChoiceCard active={!sameGraphics} onClick={() => setSameGraphics(false)}
            title="رسومات جديدة" desc="نولّد صورًا جديدة لكل طفل اعتمادًا على النص، مع الحفاظ على النمط." />
          <StepNav onBack={() => setStep(0)} onNext={() => setStep(2)} />
        </div>
      )}

      {step === 2 && (
        <div className="form-zone" style={{ maxWidth: 640 }}>
          <h2 className="page-title" style={{ fontSize: '1.05rem' }}>الإعدادات</h2>
          <div className="field">
            <label>عدد الأطفال</label>
            <input type="number" min="1" max="60" value={count} onChange={(e) => setCount(e.target.value)} />
          </div>
          <div className="field">
            <label>عنوان السلسلة</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثلاً: روضة الأمل - الفوج أ" />
          </div>
          <div className="field">
            <label>السعر الافتراضي لكل كتاب (₪)</label>
            <input type="number" min="0" value={defaultPrice} onChange={(e) => setDefaultPrice(e.target.value)} placeholder="مثلاً 200" />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.9rem', marginTop: 6 }}>
            <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />
            سلسلة تجريبية (لا تُحتسب في الإحصائيات)
          </label>
          <StepNav onBack={() => setStep(1)} onNext={() => { buildNames(); setStep(3); }} nextLabel="التالي" />
        </div>
      )}

      {step === 3 && (
        <div style={{ maxWidth: 640 }}>
          <h2 className="page-title" style={{ fontSize: '1.05rem' }}>الأطفال (اختياري)</h2>
          <p className="admin-scene-subtitle" style={{ marginTop: 0 }}>
            يمكنك تعبئة الأسماء الآن أو لاحقًا. الصور والأسعار والتوليد تتم في صفحة إدارة السلسلة بعد الإنشاء.
          </p>
          <div style={{ display: 'grid', gap: 10 }}>
            {names.map((m, i) => (
              <div key={i} style={{
                display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
                padding: '10px 12px', border: '1px solid #eee', borderRadius: 12, background: '#fff',
              }}>
                <span style={{ width: 22, color: '#9ca3af', fontWeight: 700 }}>{i + 1}</span>
                <input type="text" value={m.name} onChange={(e) => setName(i, { name: e.target.value })}
                  placeholder="اسم الطفل (اختياري)" style={{ flex: '1 1 180px', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                <select value={m.gender} onChange={(e) => setName(i, { gender: e.target.value })}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
                <input type="number" min="1" max="14" value={m.age} onChange={(e) => setName(i, { age: e.target.value })}
                  placeholder="العمر" style={{ width: 72, padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
              </div>
            ))}
          </div>
          {error && <p style={{ color: '#dc2626', marginTop: 10 }}>{error}</p>}
          <div className="admin-scene-actions" style={{ marginTop: 16 }}>
            <button type="button" className="ghost-btn" onClick={() => setStep(2)}>رجوع</button>
            <button type="button" className="submit-btn" onClick={submit} disabled={submitting}>
              {submitting ? 'جارِ الإنشاء…' : 'إنشاء السلسلة وإدارتها'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChoiceCard({ active, onClick, title, desc }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        display: 'block', width: '100%', textAlign: 'right', cursor: 'pointer',
        margin: '8px 0', padding: '14px 16px', borderRadius: 14,
        border: active ? '2px solid #7c3aed' : '1px solid #e5e7eb',
        background: active ? '#f5f3ff' : '#fff',
      }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{desc}</div>
    </button>
  );
}

function StepNav({ onBack, onNext, nextLabel }) {
  return (
    <div className="admin-scene-actions" style={{ marginTop: 16 }}>
      {onBack && <button type="button" className="ghost-btn" onClick={onBack}>رجوع</button>}
      {onNext && <button type="button" className="submit-btn" onClick={onNext}>{nextLabel || 'التالي'}</button>}
    </div>
  );
}
